/**
 * Display formatting helpers.
 *
 * The API serves ISO 8601 UTC timestamps (`docs/API.md`); everything shown to
 * a researcher is a short English phrase, never a raw timestamp.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * Format a duration in the console's house style.
 *
 * Examples: `45 s`, `12 min`, `1 h 04`, `2 h`, `3 d 04 h`.
 *
 * Args:
 *     milliseconds: Duration to format; negative values are clamped to zero.
 *
 * Returns:
 *     A short, human-readable duration.
 */
export function formatDuration(milliseconds: number): string {
  const value = Math.max(0, Math.round(milliseconds));
  if (value < MINUTE_MS) {
    return `${Math.floor(value / 1000)} s`;
  }
  if (value < HOUR_MS) {
    return `${Math.floor(value / MINUTE_MS)} min`;
  }
  if (value < DAY_MS) {
    const hours = Math.floor(value / HOUR_MS);
    const minutes = Math.floor((value % HOUR_MS) / MINUTE_MS);
    return minutes === 0
      ? `${hours} h`
      : `${hours} h ${String(minutes).padStart(2, "0")}`;
  }
  const days = Math.floor(value / DAY_MS);
  const hours = Math.floor((value % DAY_MS) / HOUR_MS);
  return hours === 0
    ? `${days} d`
    : `${days} d ${String(hours).padStart(2, "0")} h`;
}

/**
 * Milliseconds elapsed since an ISO timestamp.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     Milliseconds elapsed, or null when the input is null or unparseable.
 */
export function elapsedSince(
  iso: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!iso) {
    return null;
  }
  const timestamp = Date.parse(iso);
  return Number.isNaN(timestamp) ? null : now - timestamp;
}

/**
 * Milliseconds remaining until an ISO timestamp.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     Milliseconds remaining (negative when past), or null when unparseable.
 */
export function remainingUntil(
  iso: string | null | undefined,
  now: number = Date.now(),
): number | null {
  const elapsed = elapsedSince(iso, now);
  return elapsed === null ? null : -elapsed;
}

/**
 * Format the time left before a deadline.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *     now: Reference instant in milliseconds.
 *
 * Returns:
 *     e.g. `7 h 20`, `now` when the deadline has passed, `—` when unknown.
 */
export function formatRemaining(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  const remaining = remainingUntil(iso, now);
  if (remaining === null) {
    return "—";
  }
  return remaining <= 0 ? "now" : formatDuration(remaining);
}

/**
 * Format the time elapsed since a timestamp.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *     now: Reference instant in milliseconds.
 *
 * Returns:
 *     e.g. `3 h 12`, or `—` when unknown.
 */
export function formatElapsed(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  const elapsed = elapsedSince(iso, now);
  return elapsed === null ? "—" : formatDuration(elapsed);
}

/**
 * Format a wall-clock time, e.g. `18:40`.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *
 * Returns:
 *     The local time, or `—` when unknown.
 */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) {
    return "—";
  }
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

/**
 * Format an absolute date and time, for timelines and tooltips.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *
 * Returns:
 *     e.g. `27 Jul 2026, 14:03`, or `—` when unknown.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) {
    return "—";
  }
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

/**
 * Pluralize a count with its unit.
 *
 * Args:
 *     count: The number.
 *     singular: Unit in the singular.
 *     plural: Unit in the plural; defaults to `singular + "s"`.
 *
 * Returns:
 *     e.g. `1 GPU`, `4 GPUs`.
 */
export function pluralize(
  count: number,
  singular: string,
  plural = `${singular}s`,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Format a GPU-hours figure without pretending to a precision it lacks.
 *
 * Used by the admin screen's usage column — a spend report, not a quota.
 *
 * Args:
 *     hours: A number of GPU-hours.
 *
 * Returns:
 *     e.g. `120`, `88.5`.
 */
export function formatGpuHours(hours: number): string {
  if (!Number.isFinite(hours)) {
    return "—";
  }
  const rounded = Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Format a declared hourly rate, e.g. `€5.40`.
 *
 * Always a rate for the fleet and never anybody's total: the console publishes
 * what the machines cost, and there is no per-person figure in this API to
 * format. The two decimals are kept even when they are zeros, because a price
 * that reads `€5` beside one that reads `€5.40` looks rounded rather than
 * exact.
 *
 * Args:
 *     value: Euros per hour, as the server declared them.
 *
 * Returns:
 *     e.g. `€5.40`, or `—` when the number is not one.
 */
export function formatEuroPerHour(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a percentage measured by telemetry.
 *
 * Args:
 *     value: Percentage, or null when telemetry is unavailable.
 *
 * Returns:
 *     e.g. `96%`, or `—` when the value was not measured.
 */
export function formatPercent(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : `${Math.round(value)}%`;
}

